import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Container,
  LineContainer,
  Line,
  Property,
  Values,
  Value,
  InnerValue,
  ColorSwatch,
  Punctuation,
} from './CodeBlock.styles'
import { CSStyles, InnerPropertyStyle } from '../../useGenerateLayerCSS'

export interface CodeBlockProps {
  styles: CSStyles
  className?: string
}

export const CodeBlock = ({ styles, className }: CodeBlockProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const innerContainerRef = useRef<HTMLDivElement>(null)
  const [containerStyle, setContainerStyle] = useState({})

  useEffect(() => {
    /**
     * Format clipboard content given our html structure within the code block all elements are copied with a \n
     */
    const formatClipboard = (event: ClipboardEvent) => {
      event.preventDefault()

      const selection = window.getSelection()
      const formattedText = selection
        ?.toString()
        .trim()
        .replace(/\n/g, ' ')
        .split(/\s*;\s*/)
        .join(';\n')

      if (formattedText) {
        navigator.clipboard.writeText(formattedText)
      }
    }

    const div = containerRef.current
    if (div) {
      div.addEventListener('copy', formatClipboard)
    }

    return () => {
      if (div) {
        div.removeEventListener('copy', formatClipboard)
      }
    }
  }, [])

  useEffect(() => {
    const updateContainerHeight = () => {
      if (innerContainerRef.current && innerContainerRef.current) {
        const contentHeight = innerContainerRef.current.offsetHeight
        if (contentHeight > 130) {
          // Set fixed height and enable scroll when content exceeds 130px
          setContainerStyle({
            minHeight: '130px',
            height: '130px',
            overflowY: 'auto',
            resize: 'vertical',
          })
        } else {
          setContainerStyle({
            height: 'auto',
            overflowY: 'visible',
          })
        }
      }
    }

    updateContainerHeight()

    // Re-check on content changes or window resize
    window.addEventListener('resize', updateContainerHeight)

    // Update container styles when content changes
    const observer = new MutationObserver(updateContainerHeight)
    if (innerContainerRef.current) {
      observer.observe(innerContainerRef.current, {
        childList: true,
        subtree: true,
      })
    }

    return () => {
      window.removeEventListener('resize', updateContainerHeight)
      observer.disconnect()
    }
  }, [])

  const renderedStyles = useMemo(() => {
    const renderValue = (
      styleValue: InnerPropertyStyle,
      lastItem: boolean = true
    ) => {
      if (styleValue.innerProperty) {
        const { property, values } = styleValue.innerProperty

        const innerValues = values.map((iv, index) => {
          return (
            <Value key={`inner-value-${index}`}>
              {iv.value ? <InnerValue>{iv.value}</InnerValue> : undefined}
              {iv.color ? (
                <>
                  <ColorSwatch background={iv.color} spaceLeft={!!iv.value} />
                  <InnerValue>{iv.color}</InnerValue>
                </>
              ) : undefined}
              {index < values.length - 1 ? (
                <Punctuation>,&nbsp;</Punctuation>
              ) : undefined}
            </Value>
          )
        })

        return (
          <>
            {property ? <Value>{property}(</Value> : undefined}
            {innerValues}
            {property ? <Value>)</Value> : undefined}
            <Punctuation>{lastItem ? <>;</> : <>,&nbsp;</>}</Punctuation>
          </>
        )
      }

      return (
        <Value>
          {styleValue.value ? (
            <InnerValue>{styleValue.value}</InnerValue>
          ) : undefined}
          {styleValue.color ? (
            <>
              <ColorSwatch
                background={styleValue.color}
                spaceLeft={!!styleValue.value}
              />
              <InnerValue>{styleValue.color}</InnerValue>
            </>
          ) : undefined}
          {lastItem ? (
            <Punctuation>;</Punctuation>
          ) : (
            <Punctuation>,&nbsp;</Punctuation>
          )}
        </Value>
      )
    }

    return styles.map((style, index) => {
      return (
        <LineContainer key={`line-${style.property}-${index}`}>
          <Line>
            <Property>{style.property}:</Property>
            <Values>
              {style.values.map((value, index) => (
                <React.Fragment key={`line-value-${index}`}>
                  {renderValue(value, index === style.values.length - 1)}
                </React.Fragment>
              ))}
            </Values>
          </Line>
        </LineContainer>
      )
    })
  }, [styles])

  return (
    <Container ref={containerRef} className={className} style={containerStyle}>
      <div ref={innerContainerRef}>{renderedStyles}</div>
    </Container>
  )
}
